import { parseUrl } from '@b2b/common/filters';
import { ApiService } from '@b2b/common/services/api/types';
import { FeatureService } from '@b2b/common/services/feature';
import { TODO } from '@b2b/types/core';
import { isPrimaryCardholder, MembershipCardholder, MembershipDetails } from '@b2b/types/membership';
import { SubscriptionDetail } from '@b2b/types/subscriptions';
import { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IPromise, IQService } from 'angular';
import invariant from 'tiny-invariant';
import { MembershipDetailsVM } from '../controllers/types';
import { Order } from '@b2b/types/order';

export class MembershipDetailService {
  static $inject = ['$state', '$filter', '$q', 'ApiService', 'FeatureService'];

  filterCurrency = this.$filter('currency');

  constructor(
    private $state: StateService,
    private $filter: IFilterService,
    private $q: IQService,
    private apiService: ApiService,
    private featureService: FeatureService
  ) {}

  fetchLevelDetails(categoryId: string) {
    return this.apiService.membership.categories.detail(categoryId);
  }

  process(data: MembershipDetails, subscriptionDetail: SubscriptionDetail | null) {
    const vm = { ...data } as MembershipDetailsVM;

    if (!Array.isArray(vm.cardholderAttributes)) {
      vm.cardholderAttributes = [];
    }

    if (!Array.isArray(vm.cardholders)) {
      vm.cardholders = [];
    }

    vm.primaryCardholder = angular.copy(vm.cardholders.find(isPrimaryCardholder));

    if (this.featureService.isEnabled(this.featureService.customers) && vm.primaryCardholder?.customerId) {
      vm.primaryCardholder.acmeCustomerNumber = parseUrl(
        this.$state.href('application.customers.individuals.detail', { id: vm.primaryCardholder.customerId }),
        vm.primaryCardholder.acmeCustomerNumber
      );
    }

    if (vm.primaryCardholder?.details) {
      vm.details = vm.primaryCardholder.details;
    }

    // Tack on the subscription data if present
    if (subscriptionDetail) {
      let nextPayment = subscriptionDetail.membership.price / 100;
      const priceLists = subscriptionDetail.membershipOffering.priceLists;

      if (
        Array.isArray(priceLists) &&
        priceLists.length &&
        Array.isArray(priceLists[0].prices) &&
        priceLists[0].prices.length
      ) {
        nextPayment = this.getNextPayment(subscriptionDetail.membership.pricePointId, priceLists[0].prices);
      }
      const href = this.$state.href('application.subscriptions.detail', { id: vm.id });
      vm.subscriptionId = parseUrl(href, subscriptionDetail.recurringPaymentPlan.id);
      vm.subscriptionStatus = subscriptionDetail.recurringPaymentPlan.status;
      vm.subscriptionStatusChangeDate = subscriptionDetail.recurringPaymentPlan.statusChangeDate;

      if (this.isActiveSubscription(subscriptionDetail)) {
        vm.nextPaymentDate = subscriptionDetail.recurringPaymentPlan.nextPaymentDate;
        vm.nextPayment = this.filterCurrency(nextPayment);
        vm.cardOnFile = this.mapCardOnFile(subscriptionDetail.onfilePaymentMethod);
      }
    }

    vm.isLatestString = vm.lastVersion ? 'Yes' : 'No';

    return vm;
  }

  fetchOrderDetails(orderId: number | undefined | null): IPromise<TODO> {
    const q = this.$q.defer();

    if (orderId) {
      this.apiService.orders.willCall
        .order(orderId)
        .then((response) => {
          const order: Order = response.data.payload;
          const orderLink = parseUrl(
            this.$state.href('application.orders.detail.summary', { orderId: order.orderId }),
            order.orderNumber
          );
          q.resolve(orderLink);
        })
        .catch(() => {
          q.resolve('');
        });
    } else {
      q.resolve('');
    }

    return q.promise;
  }

  isActiveSubscription(subscriptionDetail: SubscriptionDetail | null) {
    return subscriptionDetail?.recurringPaymentPlan.status === 'active';
  }

  createSwappedPrimaryCardholderModel(
    membership: Partial<MembershipDetailsVM>,
    primaryCardholder: MembershipCardholder
  ) {
    const model = angular.copy(membership);
    model.cardholders = model.cardholders ?? [];

    model.cardholders = model.cardholders.map((cardholder) => {
      if (cardholder.id !== primaryCardholder.id) {
        return { ...cardholder, cardType: 'secondary', primaryCard: 'no' };
      }

      return { ...cardholder, cardType: 'primary', primaryCard: 'yes' };
    });

    return model;
  }

  addNewPrimaryCardholder(membership: Partial<MembershipDetailsVM>, cardholder: MembershipCardholder) {
    const model = angular.copy(membership);
    model.cardholders = model.cardholders ?? [];
    const originalCardholderIds = new Set(model.cardholders.map((cardholder) => cardholder.id));

    /**
     * First add the new primary cardholder as a secondary to allow the BE to persist it.
     * Else we get the following error:
     * "One primary cardholder must remain active through the membership expiration date."
     */
    model.cardholders.push({ ...cardholder, cardType: 'secondary', primaryCard: 'no' });

    return this.apiService.membership.update(model).then((response) => {
      const newlyPersistedCardholder = response.data.payload.cardholders.find(
        (cardholder) => !originalCardholderIds.has(cardholder.id)
      );

      if (newlyPersistedCardholder) {
        const payload = this.createSwappedPrimaryCardholderModel(
          { ...model, cardholders: response.data.payload.cardholders },
          newlyPersistedCardholder
        );

        return this.apiService.membership.update(payload).then((response) => response.data.payload);
      }

      return response.data.payload;
    });
  }

  addNewSecondaryCardholder(membership: Partial<MembershipDetailsVM>, cardholder: MembershipCardholder) {
    const model = angular.copy(membership);
    model.cardholders = model.cardholders ?? [];

    model.cardholders.push(cardholder);

    return this.apiService.membership.update(model).then((response) => response.data.payload);
  }

  getCardholderByCardImportId(membership: MembershipDetails, cardImportId: string) {
    const cardholders = Array.isArray(membership.cardholders) ? membership.cardholders : [];
    const index = cardholders.findIndex((cardholder) => cardholder.cardImportId === cardImportId);
    const member = cardholders[index];

    invariant(member, `Couldn't find member ${cardImportId}`);

    return { index, member };
  }

  private getNextPayment(pricePointId: string, prices: TODO[]) {
    let nextPayment;

    for (let i = 0, ii = prices.length; i < ii; i++) {
      if (prices[i].personType.id === pricePointId) {
        nextPayment = parseFloat(prices[i].price);
        break;
      }
    }

    return nextPayment;
  }

  private mapCardOnFile(onfilePaymentMethod: SubscriptionDetail['onfilePaymentMethod']) {
    if (onfilePaymentMethod == null) {
      return '';
    }

    return `${onfilePaymentMethod.paymentMethodBrand} x${onfilePaymentMethod.ccLastFourDigits} ${onfilePaymentMethod.holderName}`;
  }
}

export const MembershipDetailServiceModule = angular
  .module('acme.view.application.membership.detail.service', ['acme.common.service.api', 'acme.common.service.feature'])
  .service('MembershipDetailService', MembershipDetailService).name;
